import store from '@/store'

import _forEach from 'lodash/forEach'
import _max from 'lodash/max'
import { formatMoney } from '@/utils/currency'
import { trRegex, roundToThree } from '@/utils/helper'

export const GridDataTableItemType = {
  CURRENCY: 1,
  RATIO: 2
}

export const GridDataTableFooterItemType = {
  TEXT: 1,
  SUM: 2,
  AVERAGE: 3
}

export const GridDataTableColumnType = {
  SINGLE_LINE_TEXT: 1,
  DOUBLE_LINE_TEXT: 2
}

export const SortDirection = {
  ASC: 1,
  DESC: 2
}

export class GridDataTableItem {
  constructor (data = {}) {
    this.id = data.id || null
    this.columnKey = data.columnKey
    this.rowKey = data.rowKey
    this.value = data.value
    this.displayValue = data.displayValue || data.value
    this.searchIndex = data.searchIndex
    this.isFooterItem = data.isFooterItem || false
    this.isHighlighted = data.isHighlighted // Only for row highlighting
    // this.isExpandable = data.isExpandable || false
    this.rowHeightPivotItem = data.rowHeightPivotItem || false

    this.isExpanded = data.isExpanded || false
    this.isSubItem = data.isSubItem || false
    this.subItems = data.subItems || []

    this.useDefaultContent = data.useDefaultContent !== undefined ? data.useDefaultContent : true

    this.isInactive = data.isInactive || false
    this.disableClickIfInactive = data.disableClickIfInactive || false
  }
}

export class GridDataTableRow {
  constructor (data = {}) {
    this.key = data.key
    this.isActiveKey = data.isActiveKey || null
    this.subRowListKey = data.subRowListKey || null
    this.rowData = data.rowData
    this.itemMap = data.itemMap
    this.isExpanded = data.isExpanded || false
    this.isFooter = data.isFooter || false
  }

  compare (otherRow, key) {
    const value = this.itemMap[key].value
    const otherValue = otherRow.itemMap[key].value
    if (otherValue > value)
      return -1
    else if (otherValue < value)
      return 1
    return 0
  }

  static comparator (key, direction) {
    const directionModifier = direction === SortDirection.ASC ? 1 : -1
    return (row1, row2) => {
      return row1.compare(row2, key) * directionModifier
    }
  }

  // set isExpandable (isExpandable) {
  //   this._isExpandable = isExpandable
  //   for (let columnKey in this.itemMap) {
  //     let item = this.itemMap[columnKey]
  //     item.isExpandable = isExpandable
  //   }
  // }
  //
  // get isExpandable () {
  //   return this._isExpandable
  // }

  set isExpanded (isExpanded) {
    this._isExpanded = isExpanded
    for (let columnKey in this.itemMap) {
      let item = this.itemMap[columnKey]
      item.isExpanded = isExpanded
    }
  }

  get isExpanded () {
    return this._isExpanded
  }
}

export class GridDataTableColumn {
  constructor (data = {}) {
    this.key = data.key || null
    this.type = data.type || null
    this.text = data.text || null
    this.firstLineText = data.firstLineText || null
    this.secondLineText = data.secondLineText || null
    this.textAlign = data.textAlign || null
    this.sortKey = data.sortKey || null
    this.sortDirection = data.sortDirection || SortDirection.DESC
    this.isSortable = data.isSortable !== undefined ? data.isSortable : false
    this.isActiveSortColumn = data.isActiveSortColumn || false
    this.isVisible = data.isVisible !== undefined ? data.isVisible : true
    this.isArrangable = data.isArrangable || false
    this.hasLeftBorder = data.hasLeftBorder || false

    this.useDefaultHeader = data.useDefaultHeader !== undefined ? data.useDefaultHeader : true
  }

  equals (otherColumn) {
    return otherColumn && otherColumn.key === this.key
  }

  changeSortDirection = () => {
    this.sortDirection = this.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
  }

  get hasSingleLineText () {
    return this.type === GridDataTableColumnType.SINGLE_LINE_TEXT
  }

  get hasDoubleLineText () {
    return this.type === GridDataTableColumnType.DOUBLE_LINE_TEXT
  }

  get fullText () {
    return this.hasSingleLineText ? this.text : (this.firstLineText + " " + this.secondLineText)
  }

}

export class GridDataTableColumnGroup {
  constructor (data = {}) {
    this.id = data.id || null
    this.disableStateSave = data.disableStateSave || false
    this.columns = data.columns || null // [GridDataTableColumn]
    this.isFixed = data.isFixed || false
    this.isArrangable = data.isArrangable || false
    this.title = data.title || null

    this.loadState()
  }

  get canSaveState () {
    return !!(this.isArrangable && !this.disableStateSave && this.id)
  }

  get visibleColumns () {
    return this.columns.filter((column) => column.isVisible)
  }

  get nonArrangableItemIndexes () {
    let result = []
    for (let i = 0; i < this.columns.length; i++) {
      let column = this.columns[i]
      if (!column.isArrangable)
        result.push(i)
    }
    return result
  }

  get arrangableColumns () {
    return this.columns.filter(c => c.isArrangable)
  }

  set arrangableColumns (arrangableColumns) {
    let clone = Object.assign([], arrangableColumns)
    for (let i = 0; i < this.nonArrangableItemIndexes.length; i++) {
      const index = this.nonArrangableItemIndexes[i];
      clone.splice(index, 0, this.columns[index])
    }
    this.columns = clone
  }

  saveState () {
    if (this.canSaveState && this.columns && this.columns.length) {
      const user = store.state.App.user

      let columnConfigState = {}
      _forEach(this.columns, (c, i) => {
        columnConfigState[c.key] = {
          order: i,
          isVisible: c.isVisible
        }
      })


      let ccState = localStorage.getItem('CCS_' + user.id)
      if (!ccState) {
        ccState = {}
      } else {
        ccState = JSON.parse(ccState)
      }

      ccState[this.id] = columnConfigState
      localStorage.setItem('CCS_' + user.id, JSON.stringify(ccState))
    }
  }

  loadState () {
    if (this.canSaveState) {
      const user = store.state.App.user

      let ccState = localStorage.getItem('CCS_' + user.id)
      if (ccState) {
        ccState = JSON.parse(ccState)

        let columnConfigState = ccState[this.id]
        if (columnConfigState) {
          _forEach(this.columns, c => {
            if (columnConfigState[c.key]) {
              c.isVisible = columnConfigState[c.key].isVisible
            }
          })

          this.columns = this.columns.sort((a, b) => {
            let a_order = 999
            let b_order = 999
            if (columnConfigState[a.key]) {
              a_order = columnConfigState[a.key].order
            }
            if (columnConfigState[b.key]) {
              b_order = columnConfigState[b.key].order
            }

            return a_order - b_order
          })
        }
      }
    }
  }
}

export class GridDataTable {
  constructor (data = {}) {
    this.columnGroups = data.columnGroups || []
    this.rows = data.rows || []
    this.isLocal = data.isLocal
    this.arrangeTableWide = data.arrangeTableWide

    this.searchPlaceholder = data.searchPlaceholder
    this.searchKeyword = data.searchKeyword
    this.searchColumnKeys = data.searchColumnKeys || []
    this.hoveredRowKey = data.hoveredRowKey || null
    this.hoveredColumnKey = data.hoveredColumnKey || null
    this.footerRow = data.footerRow || null // Function that takes all rows as argument, returns the footer row

    this.isLoading = data.isLoading
    this.showLoading = data.showLoading
    this.emptyResultText = data.emptyResultText

    this.highlightColumns = data.highlightColumns !== undefined ? data.highlightColumns : true
    this.highlightRows = data.highlightRows !== undefined ? data.highlightRows : true
    this.highlightCell = data.highlightCell !== undefined ? data.highlightCell : true
    this.rowClickEnabled = data.rowClickEnabled || false
    this.alwaysShowHeader = data.alwaysShowHeader || false
    this.autoArrangeRowHeights = data.autoArrangeRowHeights || false
    this.showColumnGroupTitle = data.showColumnGroupTitle || false

    this.rowHeights = {}

    // Pagination
    this.paginate = data.paginate || false
    this.page = data.page || 1
    this.pageLimit = data.pageLimit || 50
    this.paginationItemCount = data.paginationItemCount || 0
  }

  get columns () {
    return this.columnGroups.reduce((prev, current) => prev.concat(current.columns), [])
  }

  get visibleColumns () {
    return this.columns.filter((column) => column.isVisible)
  }

  get activeSortColumn () {
    return this.visibleColumns.find((column) => column.isActiveSortColumn)
  }

  get sortDirection () {
    return this.activeSortColumn.sortDirection
  }

  get activeSortColumnKey () {
    let column = this.activeSortColumn
    let columnKey = column.sortKey || column.key
    if (column.sortDirection === SortDirection.DESC) columnKey = '-' + columnKey

    return columnKey
  }

  get fixedColumnGroups () {
    return this.columnGroups.filter((cg) => cg.isFixed)
  }

  get scrollableColumnGroups () {
    return this.columnGroups.filter((cg) => !cg.isFixed)
  }

  get arrangableColumnGroup () {
    for (let i = 0; i < this.columnGroups.length; i++) {
      if (this.columnGroups[i].isArrangable)
        return this.columnGroups[i]
    }
    return null
  }

  get searchFilteredRows () {
    if (!this.searchKeyword || !this.searchColumnKeys || !this.searchColumnKeys.length)
      return this.rows

    let result = []
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i]

      let searchableItems = []
      for (let j = 0; j < this.searchColumnKeys.length; j++) {
        const searchColumnKey = this.searchColumnKeys[j]
        const item = row.itemMap[searchColumnKey]
        searchableItems.push(row.itemMap[searchColumnKey])
      }

      let rowMeetsSearchCriteria = false
      for (let j = 0; j < searchableItems.length; j++) {
        const searchableItem = searchableItems[j]
        if (trRegex(this.searchKeyword).test(searchableItem.searchIndex)) {
          rowMeetsSearchCriteria = true
          break;
        }
      }

      if (rowMeetsSearchCriteria)
        result.push(row)
    }

    return result
  }

  get orderedRows () {
    if (!this.activeSortColumn) return this.searchFilteredRows
    return Object.assign([], this.searchFilteredRows).sort(GridDataTableRow.comparator(this.activeSortColumn.key, this.activeSortColumn.sortDirection))
  }

  get displayedRowsWithoutFooterRow () {
    if (this.isLocal) {
      const orderedRows = this.orderedRows
      if (this.paginate) {
        this.paginationItemCount = orderedRows.length
        return orderedRows.slice(
          this.pageLimit * (this.page - 1),
          this.pageLimit * (this.page - 1) + this.pageLimit
        )
      }
      return orderedRows
    }
    return this.rows
  }

  get displayedRows () {
    const displayedRowsWithoutFooterRow = this.displayedRowsWithoutFooterRow
    if (this.footerRow && displayedRowsWithoutFooterRow && displayedRowsWithoutFooterRow.length)
      return displayedRowsWithoutFooterRow.concat(this.footerRow(displayedRowsWithoutFooterRow))
    return displayedRowsWithoutFooterRow
  }

  get hasData () {
    return this.displayedRowsWithoutFooterRow && this.displayedRowsWithoutFooterRow.length
  }

  get items () {
    return this.displayedRowsWithoutFooterRow.reduce((prev, current) =>
        prev.concat(Object.keys(current.itemMap).map((key) => current.itemMap[key]))
      , [])
  }

  set hoverInfo (hoverInfo) {
    this.hoveredRowKey = hoverInfo.hoveredRowKey
    this.hoveredColumnKey = hoverInfo.hoveredColumnKey

    this.items.forEach(item => {
      item.isHighlighted = this.hoveredRowKey === item.rowKey
    });
  }

  setRowHeight (rowKey, columnKey, itemHeight) {
    if (!this.rowHeights.hasOwnProperty(rowKey)) {
      this.rowHeights[rowKey] = {}
    }
    this.rowHeights[rowKey][columnKey] = itemHeight
  }

  getRowHeight (rowKey) {
    let columnHeights = Object.values(this.rowHeights[rowKey])
    return _max(columnHeights)
  }
}

export class GridDataTableFactory {
  constructor () {
    this.response = []
    this.GRID_DEFINITION = {}
  }

  create () {
    let column_groups = this._createColumnGroups()
    let rows = this._createRows()
    return new GridDataTable({
      columnGroups: column_groups,
      rows: rows,
      footerRow: this.GRID_DEFINITION.gridDataTable.hideFooter ? null : this._createFooterRow(this),
      emptyResultText: this.$t('generic.msg.no_data'),
      alwaysShowHeader: true,
      ...this.GRID_DEFINITION.gridDataTable
    })
  }

  addNewRows = (gridDataTable) => {
    gridDataTable.rows = gridDataTable.rows.concat(this._createRows())
  }

  recreateRows = (gridDataTable) => {
    gridDataTable.rows = this._createRows()
  }

  _createColumnGroups = () => {
    let column_groups = []

    _forEach(this.GRID_DEFINITION.columnGroups, cg => {
      let columns = []
      let column_group = new GridDataTableColumnGroup(cg)

      _forEach(cg.columns, column => {
        if (column.hasOwnProperty('_text')) {
          column.text = column._text(column.key)
        }
        column.type = column.type || GridDataTableColumnType.SINGLE_LINE_TEXT
        columns.push(new GridDataTableColumn(column))
      })

      column_group.columns = columns
      column_groups.push(column_group)
    })

    return column_groups
  }

  _createRows = () => {
    let rows = []

    this.response.forEach(data => {
      let row = this._createRow(data)

      rows.push(row)
    })

    return rows
  }

  _createRow = (data) => {
    const rowKey = this.GRID_DEFINITION.row.key(data)
    let itemMap = {}

    let row = new GridDataTableRow(this.GRID_DEFINITION.row)
    row.key = rowKey
    row.rowData = data

    _forEach(this.GRID_DEFINITION.row.items, item => {
      let itemTemplate = {}
      Object.assign(itemTemplate, item)
      let parentItem = this._createItem(data, itemTemplate, row)

      // SUB ITEMS
      if (row.hasOwnProperty('subRowListKey') && data[row.subRowListKey] && data[row.subRowListKey].length > 0) {
        data[row.subRowListKey].forEach(sub_data => {
          let sub_item = this._createItem(sub_data, itemTemplate, row, true)
          parentItem.subItems.push(sub_item)
        })
      }
      itemMap[item.columnKey] = parentItem
    })
    row.itemMap = itemMap

    return row
  }

  _createItem = (data, item, row, isSubItem) => {
    if (isSubItem && item.hasOwnProperty('subItem')) {
      Object.assign(item, item.subItem)
    }

    let gridItem = new GridDataTableItem(item)
    gridItem.rowKey = row.key
    gridItem.isSubItem = !!isSubItem

    // IS ACTIVE
    if (row.hasOwnProperty('isActiveKey')) {
      gridItem.isInactive = (data[row.isActiveKey] === false)
    }

    // VALUE
    gridItem.value = data[gridItem.columnKey]
    if (item.hasOwnProperty('_value')) {
      gridItem.value = item._value(data, item)
    }

    // DISPLAY VALUE
    gridItem.displayValue = gridItem.value
    if (item.hasOwnProperty('_displayValue')) {
      gridItem.displayValue = item._displayValue(data, item)
    } else if (item.type === GridDataTableItemType.CURRENCY) {
      gridItem.displayValue = gridItem.value != null ? formatMoney(gridItem.value) : null
    } else if (item.type === GridDataTableItemType.RATIO) {
      gridItem.displayValue = gridItem.value ? gridItem.value + '%' : ''
    }

    // SEARCH INDEX
    if (gridItem.value && this.GRID_DEFINITION.gridDataTable.hasOwnProperty('searchColumnKeys')) {
      if (item.hasOwnProperty('_searchIndex')) {
        gridItem.searchIndex = item._searchIndex(data, item)
      } else {
        gridItem.searchIndex = (gridItem.value || '') + ''
      }
    } else {
      gridItem.searchIndex = null
    }

    return gridItem
  }

  _createFooterRow = (self) => (rows) => {
    let footerRow = {
      key: '_footer',
      itemMap: {},
      isFooter: true
    }

    _forEach(rows, row => {
      _forEach(row.itemMap, gridItem => {
        footerRow = self._updateFooterItem(
          footerRow,
          gridItem,
          self.GRID_DEFINITION.row.items.find(o => o.columnKey === gridItem.columnKey))
      })
    })
    return new GridDataTableRow(footerRow)
  }

  _updateFooterItem = (footerRow, gridItem, gridItemTemplate) => {
    if (!gridItem) return

    let footerItem = null
    if (!footerRow.itemMap.hasOwnProperty(gridItemTemplate.columnKey)) {
      footerItem = new GridDataTableItem(gridItemTemplate)
      footerItem.rowKey = footerRow.rowKey

      // Clear row values
      footerItem.value = ''
      footerItem.displayValue = ''
      footerItem.isFooterItem = true

      footerRow.itemMap[gridItemTemplate.columnKey] = footerItem
    } else {
      footerItem = footerRow.itemMap[gridItemTemplate.columnKey]
    }

    if (gridItemTemplate.footerItemType === GridDataTableFooterItemType.SUM) {
      footerItem.value = roundToThree((footerItem.value || 0) + (gridItem.value || 0))

    } else if (gridItemTemplate.footerItemType === GridDataTableFooterItemType.AVERAGE) {
      footerItem._total_value = (footerItem._total_value || 0) + (gridItem.value || 0)
      footerItem._item_count = (footerItem._item_count || 0) + 1

      footerItem.value = roundToThree(footerItem._total_value / footerItem._item_count)

    } else if (gridItemTemplate.footerItemType === GridDataTableFooterItemType.TEXT) {
      footerItem.value = gridItemTemplate.footerText
    }

    footerItem.displayValue = footerItem.value
    if (gridItemTemplate.type === GridDataTableItemType.CURRENCY) {
      footerItem.displayValue = formatMoney(footerItem.value)
    }

    footerRow.itemMap[gridItemTemplate.columnKey] = footerItem
    return footerRow
  }

}
